import React from 'react';
import './insights-panel.scss';

export type InsightsKpiDirection = 'up' | 'down' | 'flat';

export interface InsightsKpiRow {
  label: string;
  value: string;
  delta: string;
  direction: InsightsKpiDirection;
  positive: boolean;
  /** When true, renders with the primary (purple, larger) value style. */
  highlight?: boolean;
}

export interface InsightsFindingRow {
  label: string;
  primary: string;
  secondary?: string;
}

export interface InsightsPanelProps {
  summaryHeading?: string;
  kpiPeriodLabel?: string;
  kpis: InsightsKpiRow[];
  findings: InsightsFindingRow[];
  ariaLabel?: string;
}

const ARROW_GLYPH: Record<InsightsKpiDirection, string> = {
  up: '▲',
  down: '▼',
  flat: '–',
};

const deltaColor = (row: InsightsKpiRow): string => {
  if (row.direction === 'flat') return '#999999';
  return row.positive ? '#3a9e6f' : '#d94040';
};

/** Insights card — top of the left sidebar. Shows eyebrow, summary heading, and KPI rows. */
export const InsightsCard: React.FC<InsightsPanelProps> = ({
  summaryHeading = 'Performance summary',
  kpiPeriodLabel,
  kpis,
  ariaLabel = 'Insights',
}) => {
  return (
    <div className="insights-card" role="region" aria-label={ariaLabel}>
      <div className="insights-card__header">
        <div className="insights-card__eyebrow">Insights</div>
        <div className="insights-card__summary-heading">{summaryHeading}</div>
        {kpiPeriodLabel && (
          <div className="insights-card__period">{kpiPeriodLabel}</div>
        )}
      </div>

      <div
        className="insights-card__kpis"
        role="table"
        aria-label="Performance KPIs"
      >
        {kpis.map((row) => (
          <div
            key={row.label}
            role="row"
            className={
              row.highlight
                ? 'insights-card__kpi-row insights-card__kpi-row--primary'
                : 'insights-card__kpi-row'
            }
          >
            <span role="cell" className="insights-card__kpi-label">
              {row.label}
            </span>
            <span role="cell" className="insights-card__kpi-right">
              <span className="insights-card__kpi-value">{row.value}</span>
              {row.delta && (
                <span
                  role="cell"
                  className="insights-card__kpi-delta"
                  style={{ color: deltaColor(row) }}
                  data-direction={row.direction}
                >
                  <span aria-hidden="true">{ARROW_GLYPH[row.direction]}</span>
                  <span className="insights-card__kpi-delta-value">
                    {row.delta}
                  </span>
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Findings card — bottom of the left sidebar. Gray eyebrow, finding rows. */
export const FindingsCard: React.FC<{ findings: InsightsFindingRow[]; ariaLabel?: string }> = ({
  findings,
  ariaLabel = 'Findings',
}) => {
  return (
    <div className="findings-card" aria-label={ariaLabel}>
      <div className="findings-card__eyebrow">Findings</div>
      <ul className="findings-card__list">
        {findings.map((row) => (
          <li key={row.label} className="findings-card__row">
            <span className="findings-card__label">{row.label}</span>
            <span className="findings-card__detail">{row.primary}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Legacy wrapper — renders InsightsCard + FindingsCard stacked.
 * Kept for backwards compat with standalone Storybook stories and tests.
 */
export const InsightsPanel: React.FC<InsightsPanelProps> = (props) => (
  <div className="insights-panel-stack">
    <InsightsCard {...props} />
    <FindingsCard findings={props.findings} />
  </div>
);
